import { createContext, useContext, useState, useCallback, useRef } from "react";
import { updateShiftWithRules, createShiftWithRulesAutoApply, extractErrorMessage } from "../../lib/api";
import { toast } from "sonner";

/**
 * Calendar Edit Mode Context
 * Provides shared state and functions for calendar edit mode interactions
 * PROMPT 27: Visual-only interactions
 * PROMPT 28: Persistence and validation
 */

const CalendarEditContext = createContext(null);

// Constants for time calculations
const HOUR_HEIGHT = 60; // pixels per hour (must match ProjectCalendarPage)
const FIRST_HOUR = 6; // Calendar starts at 6 AM

/**
 * Convert pixel position to time string (HH:MM)
 */
const pixelToTime = (pixelY, containerTop) => {
  const relativeY = pixelY - containerTop;
  const hourDecimal = relativeY / HOUR_HEIGHT + FIRST_HOUR;
  const hour = Math.floor(hourDecimal);
  const minutes = Math.round((hourDecimal - hour) * 60);
  
  // Clamp to valid range
  const clampedHour = Math.max(0, Math.min(23, hour));
  const clampedMinutes = Math.max(0, Math.min(59, minutes));
  
  return `${String(clampedHour).padStart(2, '0')}:${String(clampedMinutes).padStart(2, '0')}`;
};

/**
 * Convert time string (HH:MM) to minutes since midnight
 */
const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Convert minutes since midnight to time string (HH:MM)
 */
const minutesToTime = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Calculate duration in hours between two time strings
 */
const calculateDuration = (startTime, endTime) => {
  const startMinutes = timeToMinutes(startTime);
  let endMinutes = timeToMinutes(endTime);
  
  // Handle overnight shifts
  if (endMinutes < startMinutes) {
    endMinutes += 24 * 60;
  }
  
  return (endMinutes - startMinutes) / 60;
};

export function CalendarEditProvider({ children }) {
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragPreview, setDragPreview] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Refs to access current values in callbacks without stale closures
  const dragPreviewRef = useRef(null);
  const resizePreviewRef = useRef(null);
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);
  
  // Pending changes tracking - map of shiftId -> { original, modified }
  const [pendingChanges, setPendingChanges] = useState(new Map());
  
  // Validation errors from backend - map of shiftId -> error message
  const [validationErrors, setValidationErrors] = useState(new Map());
  
  // Warning states (visual feedback only - non-blocking)
  const [warnings, setWarnings] = useState({
    overlap: false,
    outOfHours: false,
    invalidPlacement: false,
  });
  
  // Callback for refreshing data after save
  const onSaveSuccessRef = useRef(null);
  
  // New shift creation state
  const [isCreatingShift, setIsCreatingShift] = useState(false);
  const [newShiftPreview, setNewShiftPreview] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newShiftData, setNewShiftData] = useState(null);
  const isCreatingShiftRef = useRef(false);
  const newShiftPreviewRef = useRef(null);
  
  // Check if there are unsaved changes
  const hasUnsavedChanges = pendingChanges.size > 0;

  // Register callback for data refresh
  const registerOnSaveSuccess = useCallback((callback) => {
    onSaveSuccessRef.current = callback;
  }, []);

  // Toggle edit mode
  const toggleEditMode = useCallback(() => {
    if (isEditMode && hasUnsavedChanges) {
      // Show confirmation if there are unsaved changes
      if (!window.confirm("You have unsaved changes. Are you sure you want to exit edit mode? All changes will be lost.")) {
        return;
      }
    }
    setIsEditMode((prev) => !prev);
    // Clear all state when toggling
    setSelectedShiftId(null);
    setDragPreview(null);
    setResizePreview(null);
    setPendingChanges(new Map());
    setValidationErrors(new Map());
    setWarnings({ overlap: false, outOfHours: false, invalidPlacement: false });
  }, [isEditMode, hasUnsavedChanges]);

  // Enable edit mode
  const enableEditMode = useCallback(() => {
    setIsEditMode(true);
  }, []);

  // Disable edit mode
  const disableEditMode = useCallback(() => {
    setIsEditMode(false);
    setSelectedShiftId(null);
    setDragPreview(null);
    setResizePreview(null);
    setPendingChanges(new Map());
    setValidationErrors(new Map());
    setWarnings({ overlap: false, outOfHours: false, invalidPlacement: false });
  }, []);

  // Select a shift
  const selectShift = useCallback((shiftId) => {
    if (!isEditMode) return;
    setSelectedShiftId((prev) => (prev === shiftId ? null : shiftId));
  }, [isEditMode]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedShiftId(null);
  }, []);

  // Start dragging - stores original shift data
  const startDrag = useCallback((shiftId, initialPosition, shiftData) => {
    if (!isEditMode) return;
    
    const preview = {
      shiftId,
      initialPosition,
      currentPosition: initialPosition,
      shiftData, // Original shift data for time calculations
    };
    
    // Update both state and ref
    setIsDragging(true);
    isDraggingRef.current = true;
    setSelectedShiftId(shiftId);
    setDragPreview(preview);
    dragPreviewRef.current = preview;
  }, [isEditMode]);

  // Update drag position
  const updateDragPosition = useCallback((currentPosition, warnings = {}) => {
    if (!isDraggingRef.current) return;
    setDragPreview((prev) => {
      const updated = { ...prev, currentPosition };
      dragPreviewRef.current = updated;
      return updated;
    });
    setWarnings((prev) => ({ ...prev, ...warnings }));
  }, []);

  // End dragging - commit change to pending changes
  const endDrag = useCallback((containerTop, pixelDelta) => {
    // Use refs to access current values (avoids stale closure)
    const currentDragPreview = dragPreviewRef.current;
    const currentIsDragging = isDraggingRef.current;
    
    if (!currentIsDragging || !currentDragPreview) {
      setIsDragging(false);
      isDraggingRef.current = false;
      setDragPreview(null);
      dragPreviewRef.current = null;
      return;
    }
    
    const { shiftId, shiftData } = currentDragPreview;
    
    // Only commit if there was actual movement
    if (pixelDelta && Math.abs(pixelDelta.y) > 5 && shiftData) {
      // Calculate time change based on pixel movement
      const minutesDelta = Math.round((pixelDelta.y / HOUR_HEIGHT) * 60);
      
      if (minutesDelta !== 0) {
        const startMinutes = timeToMinutes(shiftData.start_time);
        const endMinutes = timeToMinutes(shiftData.end_time);
        
        const newStartMinutes = startMinutes + minutesDelta;
        const newEndMinutes = endMinutes + minutesDelta;
        
        const newStartTime = minutesToTime(newStartMinutes);
        const newEndTime = minutesToTime(newEndMinutes);
        const newDuration = calculateDuration(newStartTime, newEndTime);
        
        // Add to pending changes
        setPendingChanges((prev) => {
          const newMap = new Map(prev);
          const existing = newMap.get(shiftId);
          
          newMap.set(shiftId, {
            original: existing?.original || shiftData,
            modified: {
              ...(existing?.modified || shiftData),
              start_time: newStartTime,
              end_time: newEndTime,
              duration_hours: newDuration,
            },
          });
          
          return newMap;
        });
        
        // Clear any previous validation error for this shift
        setValidationErrors((prev) => {
          const newMap = new Map(prev);
          newMap.delete(shiftId);
          return newMap;
        });
      }
    }
    
    // Clear drag state
    setIsDragging(false);
    isDraggingRef.current = false;
    setDragPreview(null);
    dragPreviewRef.current = null;
    setWarnings({ overlap: false, outOfHours: false, invalidPlacement: false });
  }, []);

  // Cancel drag
  const cancelDrag = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
    setDragPreview(null);
    dragPreviewRef.current = null;
    setWarnings({ overlap: false, outOfHours: false, invalidPlacement: false });
  }, []);

  // Start resizing - stores original shift data
  const startResize = useCallback((shiftId, edge, initialSize, shiftData) => {
    if (!isEditMode) return;
    
    const preview = {
      shiftId,
      edge, // "start" | "end"
      initialSize,
      currentSize: initialSize,
      shiftData, // Original shift data
    };
    
    setIsResizing(true);
    isResizingRef.current = true;
    setSelectedShiftId(shiftId);
    setResizePreview(preview);
    resizePreviewRef.current = preview;
  }, [isEditMode]);

  // Update resize
  const updateResize = useCallback((currentSize, warnings = {}) => {
    if (!isResizingRef.current) return;
    setResizePreview((prev) => {
      const updated = { ...prev, currentSize };
      resizePreviewRef.current = updated;
      return updated;
    });
    setWarnings((prev) => ({ ...prev, ...warnings }));
  }, []);

  // End resizing - commit change to pending changes
  const endResize = useCallback((pixelDelta) => {
    // Use refs to access current values
    const currentResizePreview = resizePreviewRef.current;
    const currentIsResizing = isResizingRef.current;
    
    if (!currentIsResizing || !currentResizePreview) {
      setIsResizing(false);
      isResizingRef.current = false;
      setResizePreview(null);
      resizePreviewRef.current = null;
      return;
    }
    
    const { shiftId, edge, shiftData } = currentResizePreview;
    
    // Only commit if there was actual resize
    if (pixelDelta && Math.abs(pixelDelta) > 5 && shiftData) {
      const minutesDelta = Math.round((pixelDelta / HOUR_HEIGHT) * 60);
      
      if (minutesDelta !== 0) {
        let newStartTime = shiftData.start_time;
        let newEndTime = shiftData.end_time;
        
        if (edge === "start") {
          // Adjusting start time
          const startMinutes = timeToMinutes(shiftData.start_time);
          const newStartMinutes = startMinutes + minutesDelta;
          newStartTime = minutesToTime(newStartMinutes);
        } else {
          // Adjusting end time
          const endMinutes = timeToMinutes(shiftData.end_time);
          const newEndMinutes = endMinutes + minutesDelta;
          newEndTime = minutesToTime(newEndMinutes);
        }
        
        const newDuration = calculateDuration(newStartTime, newEndTime);
        
        // Validate minimum duration (30 minutes)
        if (newDuration >= 0.5) {
          // Add to pending changes
          setPendingChanges((prev) => {
            const newMap = new Map(prev);
            const existing = newMap.get(shiftId);
            
            newMap.set(shiftId, {
              original: existing?.original || shiftData,
              modified: {
                ...(existing?.modified || shiftData),
                start_time: newStartTime,
                end_time: newEndTime,
                duration_hours: newDuration,
              },
            });
            
            return newMap;
          });
          
          // Clear any previous validation error for this shift
          setValidationErrors((prev) => {
            const newMap = new Map(prev);
            newMap.delete(shiftId);
            return newMap;
          });
        }
      }
    }
    
    setIsResizing(false);
    isResizingRef.current = false;
    setResizePreview(null);
    resizePreviewRef.current = null;
    setWarnings({ overlap: false, outOfHours: false, invalidPlacement: false });
  }, []);

  // Cancel resize
  const cancelResize = useCallback(() => {
    setIsResizing(false);
    isResizingRef.current = false;
    setResizePreview(null);
    resizePreviewRef.current = null;
    setWarnings({ overlap: false, outOfHours: false, invalidPlacement: false });
  }, []);

  // Check if a shift is selected
  const isShiftSelected = useCallback((shiftId) => {
    return selectedShiftId === shiftId;
  }, [selectedShiftId]);

  // Check time validity (6am - 10pm range)
  const isTimeInValidRange = useCallback((hour) => {
    return hour >= 6 && hour <= 22;
  }, []);

  // Get pending change for a shift (for visual display)
  const getPendingChange = useCallback((shiftId) => {
    return pendingChanges.get(shiftId);
  }, [pendingChanges]);

  // Get validation error for a shift
  const getValidationError = useCallback((shiftId) => {
    return validationErrors.get(shiftId);
  }, [validationErrors]);

  // Discard all changes - restore to original state
  const discardChanges = useCallback(() => {
    setPendingChanges(new Map());
    setValidationErrors(new Map());
    setDragPreview(null);
    setResizePreview(null);
    setWarnings({ overlap: false, outOfHours: false, invalidPlacement: false });
    toast.info("All changes discarded");
  }, []);

  // Save all pending changes
  const saveChanges = useCallback(async () => {
    if (pendingChanges.size === 0) {
      toast.info("No changes to save");
      return { success: true, errors: [] };
    }
    
    setIsSaving(true);
    const errors = [];
    const savedShiftIds = [];
    
    // Process each pending change sequentially
    for (const [shiftId, change] of pendingChanges) {
      try {
        const { modified } = change;
        
        // Only send the fields that can be updated
        const updateData = {
          start_time: modified.start_time,
          end_time: modified.end_time,
          duration_hours: modified.duration_hours,
        };
        
        // EVENTSFLO-CREW-PLANNER-SHIFT-RULE-PATH-DIAGNOSTIC-V1 R1:
        // Calendar drag/resize previously hit `updateShift` (rule-less),
        // which silently bypassed the deterministic rule engine. After
        // an 8h→6h drag the Crew Chief line item retained its stale
        // 8h duration_hours, and the inclusive Meal `>=` threshold was
        // never re-applied. Routing through `updateShiftWithRules`
        // restores the same downward-recalc / canonical-default
        // behaviour that the modal save paths already enjoy.
        await updateShiftWithRules(shiftId, updateData);
        savedShiftIds.push(shiftId);
        
        // Clear validation error on success
        setValidationErrors((prev) => {
          const newMap = new Map(prev);
          newMap.delete(shiftId);
          return newMap;
        });
        
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        errors.push({ shiftId, error: errorMessage });
        
        // Store validation error for display
        setValidationErrors((prev) => {
          const newMap = new Map(prev);
          newMap.set(shiftId, errorMessage);
          return newMap;
        });
      }
    }
    
    // Remove successfully saved shifts from pending changes
    if (savedShiftIds.length > 0) {
      setPendingChanges((prev) => {
        const newMap = new Map(prev);
        for (const id of savedShiftIds) {
          newMap.delete(id);
        }
        return newMap;
      });
    }
    
    setIsSaving(false);
    
    // Show feedback
    if (errors.length === 0) {
      toast.success(`${savedShiftIds.length} shift${savedShiftIds.length !== 1 ? 's' : ''} saved successfully`);
      
      // Trigger data refresh callback
      if (onSaveSuccessRef.current) {
        onSaveSuccessRef.current();
      }
      
      return { success: true, errors: [] };
    } else if (savedShiftIds.length > 0) {
      toast.warning(`${savedShiftIds.length} saved, ${errors.length} failed`);
      
      // Trigger data refresh for partial success
      if (onSaveSuccessRef.current) {
        onSaveSuccessRef.current();
      }
      
      return { success: false, errors };
    } else {
      toast.error(`Failed to save changes: ${errors[0].error}`);
      return { success: false, errors };
    }
  }, [pendingChanges]);

  // Revert a single shift's pending change
  const revertShiftChange = useCallback((shiftId) => {
    setPendingChanges((prev) => {
      const newMap = new Map(prev);
      newMap.delete(shiftId);
      return newMap;
    });
    setValidationErrors((prev) => {
      const newMap = new Map(prev);
      newMap.delete(shiftId);
      return newMap;
    });
  }, []);

  // ============== NEW SHIFT CREATION ==============

  /**
   * Start creating a new shift by clicking on empty calendar slot
   * @param {Date} date - The date for the new shift
   * @param {number} pixelY - Y position within the calendar column
   * @param {number} containerTop - Top position of the container
   */
  const startShiftCreation = useCallback((date, pixelY, containerTop) => {
    if (!isEditMode) return;
    
    const startTime = pixelToTime(pixelY, containerTop);
    // Default end time is 1 hour after start
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = startMinutes + 60;
    const endTime = minutesToTime(endMinutes);
    
    const preview = {
      date,
      startTime,
      endTime,
      startY: pixelY,
      currentY: pixelY + HOUR_HEIGHT, // Default 1 hour height
      isDragging: true,
    };
    
    setIsCreatingShift(true);
    isCreatingShiftRef.current = true;
    setNewShiftPreview(preview);
    newShiftPreviewRef.current = preview;
  }, [isEditMode]);

  /**
   * Update the shift creation preview during drag
   * @param {number} currentY - Current Y position
   */
  const updateShiftCreation = useCallback((currentY) => {
    if (!isCreatingShiftRef.current) return;
    
    setNewShiftPreview((prev) => {
      if (!prev) return null;
      const updated = { ...prev, currentY };
      newShiftPreviewRef.current = updated;
      return updated;
    });
  }, []);

  /**
   * End shift creation drag - show the creation modal
   * @param {number} containerTop - Top position of the container
   */
  const endShiftCreation = useCallback((containerTop) => {
    const preview = newShiftPreviewRef.current;
    if (!isCreatingShiftRef.current || !preview) {
      cancelShiftCreation();
      return;
    }
    
    const { date, startY, currentY } = preview;
    
    // Calculate times from Y positions
    const topY = Math.min(startY, currentY);
    const bottomY = Math.max(startY, currentY);
    
    const startTime = pixelToTime(topY, containerTop);
    const endTime = pixelToTime(bottomY, containerTop);
    
    // Calculate duration
    const duration = calculateDuration(startTime, endTime);
    
    // Minimum duration check (15 minutes)
    if (duration < 0.25) {
      cancelShiftCreation();
      return;
    }
    
    // Prepare data for the creation modal
    const shiftData = {
      date: date.toISOString().split('T')[0],
      start_time: startTime,
      end_time: endTime,
      duration_hours: duration,
    };
    
    setNewShiftData(shiftData);
    setShowCreateModal(true);
    setIsCreatingShift(false);
    isCreatingShiftRef.current = false;
    setNewShiftPreview(null);
    newShiftPreviewRef.current = null;
  }, []);

  /**
   * Cancel shift creation
   */
  const cancelShiftCreation = useCallback(() => {
    setIsCreatingShift(false);
    isCreatingShiftRef.current = false;
    setNewShiftPreview(null);
    newShiftPreviewRef.current = null;
    setShowCreateModal(false);
    setNewShiftData(null);
  }, []);

  /**
   * Open the create modal with pre-filled time data
   * Used when clicking a "+" button for a specific time slot
   */
  const openCreateModal = useCallback((date, startTime = "09:00", endTime = "17:00") => {
    if (!isEditMode) return;
    
    const duration = calculateDuration(startTime, endTime);
    const shiftData = {
      date: typeof date === 'string' ? date : date.toISOString().split('T')[0],
      start_time: startTime,
      end_time: endTime,
      duration_hours: duration,
    };
    
    setNewShiftData(shiftData);
    setShowCreateModal(true);
  }, [isEditMode]);

  /**
   * Save the new shift
   * @param {Object} shiftData - Full shift data including project_id, location_id, name, etc.
   */
  const saveNewShift = useCallback(async (shiftData) => {
    try {
      setIsSaving(true);
      
      // EVENTSFLO-CALENDAR-CREATE-SHIFT-WITH-RULES-R2-FIX-V1
      // Rule-aware create so deterministic Meal / Crew Chief supplements
      // are materialised immediately for new shifts created via the
      // calendar create modal.
      await createShiftWithRulesAutoApply(shiftData);
      
      toast.success("Shift created successfully");
      setShowCreateModal(false);
      setNewShiftData(null);
      
      // Trigger data refresh
      if (onSaveSuccessRef.current) {
        onSaveSuccessRef.current();
      }
      
      return { success: true };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to create shift: ${errorMessage}`);
      return { success: false, error: errorMessage };
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * Close the create modal without saving
   */
  const closeCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setNewShiftData(null);
  }, []);

  const value = {
    // State
    isEditMode,
    selectedShiftId,
    isDragging,
    isResizing,
    dragPreview,
    resizePreview,
    hasUnsavedChanges,
    warnings,
    pendingChanges,
    validationErrors,
    isSaving,
    
    // New shift creation state
    isCreatingShift,
    newShiftPreview,
    showCreateModal,
    newShiftData,
    
    // Actions
    toggleEditMode,
    enableEditMode,
    disableEditMode,
    selectShift,
    clearSelection,
    startDrag,
    updateDragPosition,
    endDrag,
    cancelDrag,
    startResize,
    updateResize,
    endResize,
    cancelResize,
    isShiftSelected,
    isTimeInValidRange,
    discardChanges,
    saveChanges,
    getPendingChange,
    getValidationError,
    revertShiftChange,
    registerOnSaveSuccess,
    
    // New shift creation actions
    startShiftCreation,
    updateShiftCreation,
    endShiftCreation,
    cancelShiftCreation,
    openCreateModal,
    saveNewShift,
    closeCreateModal,
  };

  return (
    <CalendarEditContext.Provider value={value}>
      {children}
    </CalendarEditContext.Provider>
  );
}

export function useCalendarEdit() {
  const context = useContext(CalendarEditContext);
  if (!context) {
    throw new Error("useCalendarEdit must be used within a CalendarEditProvider");
  }
  return context;
}

export default CalendarEditContext;
